import React from 'react';

import { observer } from 'mobx-react';
import { reverse } from 'named-urls';
import { AxiosError } from 'axios';

import ActionCell, { ActionConfig } from 'components/Grid/components/ActionCell';
import { ColumnActions } from 'components/Grid/types';
import routes from 'core/routes';
import { UserRole } from 'core/constants';
import { useStore } from 'utils/hooks/store';
import { EscalationProfileResponse } from 'utils/api/modules/alarms/alarms.types';
import api from 'utils/api';
import { isBadRequest } from 'utils/api/errors';
import { ADMIN_AND_MANAGERS } from 'utils/roles/roleGroups';

interface Props {
  rowData: EscalationProfileResponse;
  actions: ColumnActions;
}

const EscalationProfilesActionCell: React.FC<Props> = ({ rowData, actions }) => {
  const { session, routing, notification } = useStore();

  const userRole = session.user?.role as UserRole;

  const handleDelete = async (): Promise<void> => {
    try {
      await api.alarms.deleteAlarmResponseProfile(rowData.uuid)();
      notification.enqueueSuccessSnackbar('Alarm response profile removed successfully');
      await actions.refreshData();
    } catch (e) {
      if (isBadRequest(e as AxiosError)) {
        notification.enqueueErrorSnackbar(
          'You cannot remove this alarm response profile because it is currently assigned to at least one user.'
        );
      } else {
        notification.enqueueErrorSnackbar('There was an error removing the alarm response profile');
      }
    }
  };

  const actionConfigs: ActionConfig[] = [
    {
      id: 'edit-button',
      label: !session?.user?.readOnly ? 'Edit' : 'View',
      onClick: () => {
        routing.push(
          reverse(routes.dashboard.alarms.escalationProfiles.details, { escalationProfileId: rowData.uuid })
        );
      },
    },
    {
      id: 'remove-button',
      label: 'Remove',
      visible: ADMIN_AND_MANAGERS.includes(userRole) && !session?.user?.readOnly,
      onClick: handleDelete,
      confirmation: {
        type: 'simple',
        title: 'Delete Alarm Response Profile',
        message: 'Are you sure you want to delete this alarm response profile?',
      },
    },
  ];

  return <ActionCell actions={actionConfigs} recordName={rowData.name} />;
};

export default observer(EscalationProfilesActionCell);
